import React from 'react';
import { Award, Users, Clock, ThumbsUp } from 'lucide-react';
import { stats, companyInfo } from '../data/mock';

const iconMap = {
  'Projects Completed': Award,
  'Years Experience': Clock,
  'Design Awards': Award,
  'Client Satisfaction': ThumbsUp,
};

const AboutSection = () => {
  return (
    <section id="about" className="py-24 bg-gray-900 text-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 right-20 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Grid */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1631679706909-1844bbd07221?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc2OTMyNzQwNXww&ixlib=rb-4.1.0&q=85"
                    alt="Modern Living Room"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1581783458534-001a466b5487?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NjkzMjc0MDF8MA&ixlib=rb-4.1.0&q=85"
                    alt="Elegant Bathroom"
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1599696848652-f0ff23bc911f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NjkzMjc0MDF8MA&ixlib=rb-4.1.0&q=85"
                    alt="Luxury Interior"
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1581784878214-8d5596b98a01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NjkzMjc0MDB8MA&ixlib=rb-4.1.0&q=85"
                    alt="Fireplace Lounge"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>

            {/* Experience Badge */}
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 shadow-2xl">
              <p className="text-4xl font-bold">15+</p>
              <p className="text-sm text-teal-100">Years of Excellence</p>
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
              About Us
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Creating Timeless
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-amber-400">
                {' '}Interiors{' '}
              </span>
              Since 2009
            </h2>
            <p className="text-gray-400 text-lg mb-6 leading-relaxed">
              {companyInfo.name} is a leading interior design firm dedicated to transforming
              spaces into stunning environments that reflect our clients' unique personalities
              and lifestyles.
            </p>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Our team of talented designers brings together creativity, expertise, and a deep
              understanding of design principles to deliver exceptional results. We believe that
              great design should not only be beautiful but also functional and sustainable.
            </p>

            {/* Features List */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                'Award-Winning Designs',
                'Sustainable Solutions',
                'Expert Team',
                'Client-Centric Approach',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-gray-800">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <p className="text-3xl font-bold text-amber-400">{stat.value}</p>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
